import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, Mail, Shield, Calculator, Users, HelpCircle, FileText, Home, Calendar, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import AIChatWidget from "@/components/AIChatWidget";

const PHONE = "7410121121";
const EMAIL = "Support@futuresanchay.com";
const WHATSAPP_LINK = `https://wa.me/91${PHONE}?text=Hi,%20I%20want%20to%20get%20my%20LIC%20policy%20reviewed.`;

// Floating WhatsApp Button Component
const FloatingWhatsApp = () => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
      {/* Tooltip */}
      <div className={`bg-white rounded-xl shadow-xl px-4 py-3 transition-all duration-300 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`}>
        <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">Need help? Chat with us!</p>
        <p className="text-xs text-gray-500">Typically replies within minutes</p>
      </div>
      
      {/* WhatsApp Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative group"
        data-testid="floating-whatsapp-btn"
      >
        {/* Pulse rings */}
        <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-25"></span>
        <span className="absolute inset-0 rounded-full bg-emerald-500 animate-pulse opacity-40"></span>
        
        {/* Main button */}
        <div className="relative w-14 h-14 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 group-hover:rotate-12">
          <svg 
            className="w-7 h-7 text-white" 
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </div>
        
        {/* Online indicator */}
        <span className="absolute top-0 right-0 w-4 h-4 bg-emerald-300 border-2 border-white rounded-full animate-pulse"></span>
      </a>
    </div>
  );
};

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Calculators", path: "/calculator/monthly-saving" },
  { name: "About", path: "/about" },
  { name: "FAQs", path: "/faq" },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100" data-testid="main-navigation">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Shield base */}
              <path d="M24 4L6 12V22C6 33.1 13.68 43.32 24 46C34.32 43.32 42 33.1 42 22V12L24 4Z" fill="#0ea5e9" />
              {/* Inner shield highlight */}
              <path d="M24 8L10 14.5V22C10 31.05 16.32 39.42 24 41.8C31.68 39.42 38 31.05 38 22V14.5L24 8Z" fill="#38bdf8" />
              {/* Growth bars */}
              <rect x="15" y="26" width="5" height="10" rx="1" fill="white" />
              <rect x="22" y="21" width="5" height="15" rx="1" fill="white" />
              <rect x="29" y="16" width="5" height="20" rx="1" fill="white" />
              {/* Upward arrow */}
              <path d="M32 14L35 18H29L32 14Z" fill="#fbbf24" />
              <rect x="31" y="17" width="2" height="6" fill="#fbbf24" />
            </svg>
            <div className="flex flex-col">
              <span className="font-bold text-gray-900 text-xl tracking-tight leading-none">
                Future<span className="text-sky-500">Sanchay</span>
              </span>
              <span className="text-[10px] text-gray-400 tracking-wide">TRUSTED LIC ADVISORY</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path ? "text-sky-500" : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="btn-primary h-10 px-5 text-sm" data-testid="nav-cta">
                <Calendar className="w-4 h-4 mr-2" />
                Book Free Call
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col gap-4 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`text-lg font-medium p-3 rounded-lg transition-all ${
                      location.pathname === link.path
                        ? "bg-sky-50 text-sky-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="border-t border-gray-100 pt-4 mt-2">
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp w-full"
                    data-testid="mobile-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-gray-50 border-t border-gray-100" data-testid="main-footer">
      <div className="container-main py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 4L6 12V22C6 33.1 13.68 43.32 24 46C34.32 43.32 42 33.1 42 22V12L24 4Z" fill="#0ea5e9" />
                <path d="M24 8L10 14.5V22C10 31.05 16.32 39.42 24 41.8C31.68 39.42 38 31.05 38 22V14.5L24 8Z" fill="#38bdf8" />
                <rect x="15" y="26" width="5" height="10" rx="1" fill="white" />
                <rect x="22" y="21" width="5" height="15" rx="1" fill="white" />
                <rect x="29" y="16" width="5" height="20" rx="1" fill="white" />
                <path d="M32 14L35 18H29L32 14Z" fill="#fbbf24" />
                <rect x="31" y="17" width="2" height="6" fill="#fbbf24" />
              </svg>
              <div>
                <span className="font-bold text-gray-900 text-lg">Future<span className="text-sky-500">Sanchay</span></span>
                <p className="text-[10px] text-gray-400 tracking-wide">TRUSTED LIC ADVISORY</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm">
              India's trusted LIC policy review platform. Honest advice, zero spam.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/calculator/monthly-saving" className="text-gray-500 hover:text-sky-500">Savings Calculator</Link></li>
              <li><Link to="/calculator/retirement" className="text-gray-500 hover:text-sky-500">Retirement Calculator</Link></li>
              <li><Link to="/calculator/child-education" className="text-gray-500 hover:text-sky-500">Education Calculator</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-gray-500 hover:text-sky-500">About Us</Link></li>
              <li><Link to="/faq" className="text-gray-500 hover:text-sky-500">FAQs</Link></li>
              <li><Link to="/contact" className="text-gray-500 hover:text-sky-500">Contact</Link></li>
              <li><Link to="/compliance" className="text-gray-500 hover:text-sky-500">Compliance</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href={`tel:${PHONE}`} className="hover:text-sky-500">{PHONE}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${EMAIL}`} className="hover:text-sky-500">{EMAIL}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom - Disclaimer & Copyright */}
        <div className="border-t border-gray-200 mt-8 pt-8">
          {/* Important Disclaimer */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Disclaimer:</strong> FutureSanchay® is a brand of Wise Owl Insurance & Investments, an independent insurance advisory platform. 
              We are <strong>NOT</strong> affiliated with, endorsed by, or representatives of Life Insurance Corporation of India (LIC). 
              We are IRDAI-licensed insurance distributors who provide unbiased policy review and advisory services. 
              All advice is for informational purposes only. Please read policy documents carefully before making any decisions.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-xs">
              © {new Date().getFullYear()} FutureSanchay® (Wise Owl Insurance & Investments). All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>IRDAI Licensed</span>
              </div>
              <span>|</span>
              <span>Independent Advisor</span>
              <span>|</span>
              <span>Not affiliated with LIC</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-1 pt-16 md:pt-18">
        <Outlet />
      </main>
      <Footer />
      <AIChatWidget />
    </div>
  );
};

export default Layout;
